import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { convertFromSEK } from 'src/shared/utils/currency'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertDesign = Database['public']['Tables']['design']['Insert']
export type Design_ = Database['public']['Tables']['design']['Row']

export const useDesignsApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const createDesign = useMutation({
    mutationFn: async (insertIllustration: InsertDesign) =>
      await supabase
        .from('design')
        .insert(insertIllustration)
        .select('id')
        .single(),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.designs],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quotes],
      })
      // Invalidate quote item query to reload design data
      if (variables.quote_item_id) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.quoteItemById, variables.quote_item_id],
        })
      }
      // Also invalidate all quoteItemById queries as fallback
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteItemById],
      })
    },
  })

  const updateDesign = useMutation({
    mutationFn: async (insertIllustration: InsertDesign) =>
      await supabase
        .from('design')
        .update(insertIllustration)
        .eq('id', insertIllustration.id!),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.designs] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      // Invalidate quote item query to reload design data
      if (variables.quote_item_id) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.quoteItemById, variables.quote_item_id],
        })
      }
      // Also invalidate all quoteItemById queries as fallback
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteItemById],
      })
    },
  })

  const deleteDesign = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await supabase.from('design').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.designs] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })

      // Also invalidate all quoteItemById queries as fallback
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteItemById],
      })
    },
  })

  return {
    createDesign,
    updateDesign,
    deleteDesign,
  }
}

// Hook to fetch a single design by id with its quote_item
export const useDesignById = (design_id: number | null | undefined) => {
  const supabase = useSupabaseClient()
  const currency = useOrganizationCurrency()

  const { data, isPending, error } = useQuery({
    queryKey: [queryKeys.designs, 'byId', design_id, currency],
    queryFn: async () => {
      if (!design_id) return null

      const { data, error } = await supabase
        .from('design')
        .select(
          `*,
          quote_item!design_quote_item_id_fkey(
            id,
            quote_id,
            product_variant_color_id,
            product:product_open!quote_item_product_id_fkey(
              id,
              item_name,
              item_name_translations,
              item_description,
              item_description_translations,
              source,
              moq,
              price,
              print_codes,
              product_variant(*,
                product_variant_color(*,
                  product_image(*)
                )
              )
            )
          )`
        )
        .eq('id', design_id)
        .single()

      if (error) throw error

      if (data?.quote_item && typeof data.quote_item === 'object') {
        const quoteItem = data.quote_item as { product?: { price?: number } }
        if (quoteItem.product?.price != null) {
          quoteItem.product = {
            ...quoteItem.product,
            price: convertFromSEK(quoteItem.product.price, currency) ?? 0,
          }
        }
      }

      return data
    },
    enabled: !!design_id,
  })

  return {
    design: data,
    isLoading: isPending,
    error,
  }
}
